import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { formatNum } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { ArrowUpRight, Layers, GitBranch, Flame } from "lucide-react";

const COLORS = {
  Upstream: "#D4FF00",
  Downstream: "#FFB800",
};

const fmt = (n) => formatNum(n, { decimals: 2 });

// Category → supplier → row drill-down of committed Scope 3 data (Phase 3).
function DrilldownSection({ data }) {
  const cats = (data.categories || []).filter((c) => c.data_points > 0);
  if (!cats.length) return null;
  return (
    <div className="card-flat p-6" data-testid="scope3-drilldown">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-eyebrow mb-1 flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-accent" /> Drill-down · category → supplier → line</div>
          <h3 className="font-display font-semibold text-lg">Committed Scope 3, expandable</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Expand a category to its suppliers, then to the individual committed lines. Imported lines are tagged.</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-bold">{fmt(data.total_tCO2e)}<span className="text-xs text-muted-foreground ml-1">tCO2e</span></div>
          <div className="text-[11px] text-muted-foreground">{data.line_count} line{data.line_count !== 1 ? "s" : ""} · FY{data.year}</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {cats.map((c) => (
          <details key={c.cat ?? "unc"} className="rounded border border-border bg-muted/20" data-testid={`drill-cat-${c.cat ?? "unc"}`}>
            <summary className="cursor-pointer px-3 py-2 flex items-center justify-between gap-2 hover:bg-muted/40">
              <span className="font-medium text-sm inline-flex items-center gap-2">
                <Badge variant="outline" className="text-[9px]">{c.cat != null ? `Cat ${c.cat}` : "Uncat."}</Badge>
                {c.label}
              </span>
              <span className="font-mono text-sm shrink-0">{fmt(c.total_tCO2e)} <span className="text-[10px] text-muted-foreground">tCO₂e · {c.data_points} line{c.data_points !== 1 ? "s" : ""}</span></span>
            </summary>
            <div className="px-2 pb-2 space-y-1">
              {c.suppliers.map((s, si) => (
                <details key={si} className="rounded border border-border/60 bg-background/40 ml-2" data-testid={`drill-sup-${c.cat ?? "unc"}-${si}`}>
                  <summary className="cursor-pointer px-2.5 py-1.5 flex items-center justify-between gap-2 hover:bg-muted/30">
                    <span className="text-[13px] inline-flex items-center gap-1.5"><GitBranch className="h-3 w-3 text-muted-foreground" /> {s.supplier}</span>
                    <span className="font-mono text-[12px] shrink-0">{fmt(s.total_tCO2e)} <span className="text-[10px] text-muted-foreground">· {s.data_points}</span></span>
                  </summary>
                  <div className="overflow-x-auto px-1 pb-1">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Activity</TableHead><TableHead>Period</TableHead><TableHead>Value</TableHead>
                        <TableHead>Method</TableHead><TableHead>Facility</TableHead>
                        <TableHead className="text-right">tCO2e</TableHead><TableHead>Source</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {s.rows.map((row) => (
                          <TableRow key={row.id} data-testid="drill-row">
                            <TableCell className="text-xs font-medium max-w-[200px] truncate" title={row.activity_type}>{row.activity_type}</TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">{row.period}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.value != null ? `${formatNum(row.value, { decimals: 0 })} ${row.unit}` : "—"}</TableCell>
                            <TableCell>{row.method ? <Badge variant="outline" className="text-[9px]">{row.method}</Badge> : <span className="text-[10px] text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{row.facility}</TableCell>
                            <TableCell className="text-right font-mono text-xs font-bold">{row.not_calculated ? <span className="text-muted-foreground" title="Imported, not calculated">n/c</span> : fmt(row.co2e_tonnes)}</TableCell>
                            <TableCell>{row.imported ? <Badge variant="default" className="text-[9px]" title={row.import_filename || "AI import"}>Imported</Badge> : <span className="text-[10px] text-muted-foreground">—</span>}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </details>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

export default function Scope3() {
  const [breakdown, setBreakdown] = useState(null);
  const [categories, setCategories] = useState([]);
  const [hotspots, setHotspots] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get("/scope3/breakdown?year=2024").then((r) => setBreakdown(r.data));
    api.get("/scope3/categories").then((r) => setCategories(r.data.categories));
    api.get("/scope3/hotspots?year=2024&limit=15").then((r) => setHotspots(r.data)).catch(() => {});
    api.get("/scope3/drilldown?year=2024").then((r) => setDrill(r.data)).catch(() => {});
  }, []);

  if (!breakdown) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading Scope 3 breakdown…</div>;

  const catMap = Object.fromEntries(categories.map((c) => [c.cat, c]));
  const chartData = breakdown.categories.map((r) => ({
    ...r,
    group: catMap[r.cat]?.group || "—",
    label: `${r.cat}. ${r.category.length > 22 ? r.category.slice(0, 20) + "…" : r.category}`,
  }));

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module · GHG Protocol</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Scope 3 — Value Chain</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            All 15 categories. Upstream (8) and downstream (7) emissions across the corporate value chain.
          </p>
          <a href="/scope3-bridge" className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline mt-2" data-testid="scope3-bridge-link">
            <GitBranch className="h-3.5 w-3.5" /> Open the Scope 3 Data Bridge — review supplier activity records before they enter the inventory
          </a>
        </div>
        <div className="flex gap-3">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-1 flex items-center gap-1.5">Total Scope 3 <ProvenanceTrigger pkey="scope3.total" className="!opacity-60 hover:!opacity-100" /></div>
            <div className="font-display text-2xl font-bold">{formatNum(breakdown.total_tCO2e, {decimals: 1})}<span className="text-sm text-muted-foreground ml-1">tCO2e</span></div>
          </div>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-eyebrow mb-1">Category breakdown · FY2024</div>
            <h3 className="font-display font-semibold text-lg">15-category waterfall</h3>
          </div>
          <div className="flex gap-3 text-xs font-mono">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{background: COLORS.Upstream}} /> Upstream</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{background: COLORS.Downstream}} /> Downstream</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 8, right: 0, left: -10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} angle={-35} textAnchor="end" interval={0} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
            <Tooltip />
            <Bar dataKey="total_tCO2e" radius={[2, 2, 0, 0]}>
              {chartData.map((d, i) => <Cell key={i} fill={COLORS[d.group] || "#888"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {hotspots && hotspots.hotspots && hotspots.hotspots.length > 0 && (
        <div className="card-flat p-6" data-testid="scope3-hotspots">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="label-eyebrow mb-1 flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-[#FFB800]" /> Emission hotspots · tCO₂e priority ranking</div>
              <h3 className="font-display font-semibold text-lg">Biggest value-chain lines first</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Individual Scope 3 purchase/activity lines ranked by emissions — the top rows drive your footprint.</p>
            </div>
            <div className="text-right">
              <div className="font-mono text-2xl font-bold">{formatNum(hotspots.total_tCO2e, { decimals: 1 })}<span className="text-xs text-muted-foreground ml-1">tCO2e</span></div>
              <div className="text-[11px] text-muted-foreground">{hotspots.line_count} line{hotspots.line_count !== 1 ? "s" : ""} · top {hotspots.hotspots.length} shown</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Activity line</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Basis</TableHead>
                  <TableHead className="text-right">tCO2e</TableHead>
                  <TableHead className="w-40">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hotspots.hotspots.map((h, i) => (
                  <TableRow key={h.id} data-testid="scope3-hotspot-row">
                    <TableCell className="font-mono text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="text-sm font-medium max-w-[220px] truncate">{h.activity_type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{h.cat != null ? `Cat ${h.cat} · ` : ""}{h.category}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{h.period}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{h.facility}</TableCell>
                    <TableCell>
                      {h.basis === "spend_based"
                        ? <Badge variant="warning" className="text-[9px]" data-testid="hotspot-basis-spend">Spend-based</Badge>
                        : <Badge variant="outline" className="text-[9px]">Activity</Badge>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-bold">{formatNum(h.co2e_tonnes, { decimals: 2 })}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={h.share_pct} className="h-1.5 flex-1" />
                        <span className="text-[11px] font-mono text-muted-foreground w-10 text-right">{h.share_pct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {drill && <DrilldownSection data={drill} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {breakdown.categories.map((row, i) => {
          const meta = catMap[row.cat];
          return (
            <div key={`${row.cat}-${i}`} className="card-flat p-5 group" data-testid={`cat-${row.cat}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 border border-accent/30 rounded-sm flex flex-col items-center justify-center shrink-0">
                  <span className="font-mono text-[10px] text-muted-foreground">CAT</span>
                  <span className="font-display font-bold">{row.cat}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="font-display font-semibold leading-tight">{row.category}</h4>
                    <span className="flex items-center gap-1.5 shrink-0"><ProvenanceTrigger pkey="scope3.category" pp={String(row.cat)} /><Badge variant={meta?.group === "Upstream" ? "lime" : "warning"} className="shrink-0 text-[10px]">{meta?.group}</Badge></span>
                  </div>
                  {meta?.typical_method && (
                    <div className="text-xs text-muted-foreground mb-3">{meta.typical_method}</div>
                  )}
                  <div className="flex items-end justify-between mb-2">
                    <div className="font-mono">
                      <span className="text-2xl font-bold">{formatNum(row.total_tCO2e, {decimals: 1})}</span>
                      <span className="text-xs text-muted-foreground ml-1">tCO2e</span>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">{row.share_pct}%</div>
                  </div>
                  <Progress value={row.share_pct} />
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 font-mono">{row.data_points} data point{row.data_points !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
